import { Component } from '@theme/component';

/**
 * @typedef {Object} PolicyAnchorsRefs
 * @property {HTMLAnchorElement[]} [links]
 */

/**
 * Accent-folded alphanumeric fingerprint, used to pair a link with its heading.
 * `data-heading-text` is decoded by `getAttribute`, so both sides of the
 * comparison are plain text whatever entities the policy body contains.
 * @param {string} value
 */
function fingerprint(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Anchor navigation for the store policies (cf. `sections/policy-anchors.liquid`).
 *
 * Shopify renders the policy body itself and its headings carry no `id`, so the
 * links can only resolve once this runs: it assigns the ids the server-rendered
 * list already points at.
 *
 * @extends {Component<PolicyAnchorsRefs>}
 */
class PolicyAnchors extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Module scripts are deferred, so Shopify's body — rendered after this
    // section — is already parsed by the time the element upgrades.
    this.#assignIds();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#assignIds();
  }

  #assignIds() {
    const links = this.refs.links ?? [];
    const container = document.querySelector('.shopify-policy__body');
    // Blank headings are dropped on the Liquid side too (`trim()` also drops the
    // non-breaking space), so the ordinals of both lists stay comparable for the
    // positional fallback below.
    const headings = container
      ? Array.from(container.querySelectorAll('h2')).filter((heading) => heading.textContent?.trim())
      : [];

    /** @type {Set<number>} */
    const used = new Set();
    let resolved = 0;

    links.forEach((link, index) => {
      const id = (link.getAttribute('href') ?? '').slice(1);
      const expected = fingerprint(link.dataset.headingText ?? '');

      let match = -1;
      for (let i = 0; i < headings.length; i++) {
        const heading = headings[i];
        if (!heading || used.has(i)) continue;
        if (fingerprint(heading.textContent ?? '') === expected) {
          match = i;
          break;
        }
      }

      // The rendered body can differ from the parsed one (commented-out or
      // malformed markup): fall back to the ordinal, then give up on this row
      // alone — never on the whole navigation, which would make the feature
      // vanish on exactly the policy with the messiest HTML.
      if (match === -1 && !used.has(index) && headings[index]) match = index;

      const target = match === -1 ? undefined : headings[match];

      if (!id || !target) {
        const item = link.closest('li');
        if (item instanceof HTMLElement) item.hidden = true;
        return;
      }

      used.add(match);
      target.id = id;
      // Fragment navigation only runs the focusing steps when the target is
      // focusable, so without this the focus would stay on the link and the
      // heading would never be announced.
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      resolved += 1;
    });

    if (resolved === 0) {
      this.hidden = true;
      return;
    }

    this.#jumpToCurrentHash();
  }

  /**
   * The ids only exist once the code above has run — by then the browser has
   * already given up on the fragment in the URL — so a deep link has to be
   * replayed by hand. `hashchange` needs no handling: the ids are in place then.
   */
  #jumpToCurrentHash() {
    const hash = location.hash.slice(1);
    if (!hash) return;

    let id = hash;
    try {
      id = decodeURIComponent(hash);
    } catch {
      // Malformed escape sequence: fall back to the raw hash.
    }

    // `getElementById`, never `querySelector('#' + id)`: a handleized heading
    // can start with a digit, which throws as a CSS selector.
    const target = document.getElementById(id);
    if (!target) return;

    target.scrollIntoView({ behavior: 'auto', block: 'start' });
    target.focus({ preventScroll: true });
  }
}

if (!customElements.get('policy-anchors')) {
  customElements.define('policy-anchors', PolicyAnchors);
}
